/**
 * AdminProtectedRoute - Guard pour les routes administrateur (basé sur Redux)
 *
 * Ce composant protège les routes admin et redirige vers /admin (login)
 * si l'administrateur n'est pas authentifié.
 *
 * Il utilise directement le Redux store (adminSlice) pour l'état d'authentification.
 * Il est complètement isolé de l'authentification user.
 */

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { loginSuccess, logout as adminLogout } from "@/store/slices/adminSlice";

// URL de base pour l'API admin
const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/admin`;
const ADMIN_LOGIN_ROUTE = "/admin";

// Variables globales pour éviter les appels et toasts multiples
let isVerifying = false;
let verificationPromise: Promise<boolean> | null = null;

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "admin";
  createdAt: string;
  updatedAt: string;
}

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

export const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
  children,
}) => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const hasChecked = useRef(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // État Redux pour l'authentification admin
  const { isAuthenticated: reduxIsAuthenticated, adminUser } = useAppSelector(
    (state) => state.admin,
  );

  useEffect(() => {
    // Si déjà vérifié, ne pas revérifier
    if (hasChecked.current) {
      setIsCheckingAuth(false);
      setIsAuthorized(true);
      return;
    }

    // Fonction de vérification
    const verifySession = async (): Promise<boolean> => {
      try {
        const response = await fetch(`${ADMIN_API_BASE}/me`, {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json();
        return !!(data.id && data.email);
      } catch {
        return false;
      }
    };

    const runVerification = async () => {
      // Si une vérification est déjà en cours, attendre son résultat
      if (isVerifying && verificationPromise) {
        const result = await verificationPromise;
        if (result) {
          hasChecked.current = true;
          setIsAuthorized(true);
        } else {
          // Rediriger sans toast (déjà affiché par la première vérification)
          navigate(ADMIN_LOGIN_ROUTE, { replace: true });
        }
        setIsCheckingAuth(false);
        return;
      }

      // Démarrer une nouvelle vérification
      isVerifying = true;
      verificationPromise = verifySession();

      try {
        const isValid = await verificationPromise;

        if (isValid) {
          // Session valide
          hasChecked.current = true;
          setIsAuthorized(true);

          // Synchroniser avec Redux si nécessaire
          if (!reduxIsAuthenticated || !adminUser) {
            // Récupérer les données pour Redux
            const response = await fetch(`${ADMIN_API_BASE}/me`, {
              method: "GET",
              credentials: "include",
            });
            const data = await response.json();

            const adminUserData: AdminUser = {
              id: data.id,
              email: data.email,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              role: "admin",
              createdAt: data.createdAt || new Date().toISOString(),
              updatedAt: data.updatedAt || new Date().toISOString(),
            };
            dispatch(loginSuccess(adminUserData));
          }
        } else {
          // Session invalide - UN SEUL toast
          if (reduxIsAuthenticated) {
            dispatch(adminLogout());
          }
          toast.error(
            "Session administrateur expirée. Veuillez vous reconnecter.",
          );
          navigate(ADMIN_LOGIN_ROUTE, { replace: true });
        }
      } catch (error) {
        console.error("Erreur lors de la vérification admin:", error);
        if (reduxIsAuthenticated) {
          dispatch(adminLogout());
        }
        toast.error(
          "Erreur de vérification. Redirection vers la page de connexion.",
        );
        navigate(ADMIN_LOGIN_ROUTE, { replace: true });
      } finally {
        isVerifying = false;
        verificationPromise = null;
        setIsCheckingAuth(false);
      }
    };

    runVerification();
  }, [dispatch, navigate, reduxIsAuthenticated, adminUser]);

  // Afficher un loader pendant la vérification
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen ">
        <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md backdrop-blur-md bg-white/10 dark:bg-gray-900/10 border border-white/20 dark:border-gray-700/30">
            <CardContent className="p-6">
              <div className="text-center py-12">
                <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Vérification des autorisations...
                </h3>
                <p className="text-gray-900 dark:text-gray-100">
                  Veuillez patienter
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Ne rendre les enfants que si autorisé
  if (!isAuthorized) {
    return null;
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;
